import { PriceDataType } from "@/api/PricesApi"
import { ATV_ACCEPTED_RESPONSE_CODE, CURRENCIES_CODE_IDS, DEFAULT_STORE, DEFAULT_TERMINAL, EDOCUMENT_TYPE, EDocumentTypeKey, PAYMENT_METHODS, REFERENCES_CODES, REFERENCES_INFORMATION, resolveReferencedDocumentType, SITUACION_COMPROBANTE_NORMAL, SituacionComprobanteType, TAXES_CONDITIONS_GENERAL_CREDITO_IVA_CODE, TAXES_PERCENTAGES_VALUES } from "./Constants"
import { ProductDataType } from "@/api/ProductsApi"
import { evaluate } from "mathjs";
import { BuyInvoiceProductRelatedAttributes, CreditNoteProductRelatedAttributes, DebitnoteProductRelatedAttributes, EDocumentProductDataType, InvoiceProductRelatedAttributes } from "@/formTypes/EDocumentProductDataType";
import { DocumentSequenceApi, DocumentSequenceDataType } from "@/api/DocumentSequenceApi";
import momentTz from "moment-timezone";
import { SuscriptionDataType } from "@/api/SuscriptionsApi";
import { findPropertyRecursive, generateNextNumberByModel, getMissingAttributes, omitProperties, safeEncodeBase64, toNumberFormat } from "./Utils";
import { roundCant } from "./Cant";
import { serializeStoredOtherCharges } from "./DocumentOtherCharges";
import { CreditnoteApi, CreditnoteDataType } from "@/api/CreditnoteApi";
import { DebitnoteApi, DebitnoteDataType } from "@/api/DebitnoteApi";
import { CreditnoteRelatedAttributes } from "@/formTypes/CreditnoteDataType";
import { ApiSchema } from "@/api/ApiClient";
import { DocumentAutoGeneratedNumberDataType } from "@/api/DocumentAutoGeneratedNumberApi";
import { DebitnoteRelatedAttributes } from "@/formTypes/DebitnoteDataType";
import { InvoiceApi, InvoiceDataType } from "@/api/InvoiceApi";
import { InvoiceRelatedAttributes } from "@/formTypes/InvoiceDataType";
import { CreditnoteFormDataKeys } from "@/generated/CreditnoteFormDataKeys";
import { InvoiceFormDataKeys } from "@/generated/InvoiceFormDataKeys";
import { getCurrentDateTime } from "./Api";
import { EasyposEInvoice } from "@/atv/submitInvoice";
import { ApiENoteRequest, ATVResponseBody, InfoRefElement } from "@/atv/eInvoiceApi";
import { fetchDocumentWithCompleteLines } from "@utils/pendingENoteSubmission";
import { DebitnoteFormDataKeys } from "@/generated/DebitnoteFormDataKeys";
import moment from "moment-timezone";
import { ProviderApi, ProviderDataType } from "@/api/ProviderApi";
import { CurrencyApi } from "@/api/CurrencyApi";
import { showAlert } from "./Alert";
import { OpenSendAtvDocumentPubSubPayload } from "./pubsubs/PubSub";
import { ProviderInvoiceCategoryApi } from "@/api/ProviderInvoiceCategoryApi";
import { ProviderInvoiceDataType } from "@/api/ProviderInvoiceApi";
import { ProviderInvoiceMessageFormData } from "@/formTypes/ProviderInvoiceMessageFormDataType";
import { ExpenseCategoryApi } from "@/api/ExpenseCategoryApi";
import { calculateDocumentGranTotals, updateCreditNoteDataState } from "@/store/slices/utils/eDocumentUtils";
import { EDocumentState } from "@/store/slices/eDocumentSlides";
import { BuyInvoiceApi, BuyInvoiceDataType } from "@/api/BuyInvoiceApi";
import { BuyInvoicePaymentApi } from "@/api/BuyInvoicePaymentApi";
import { BuyInvoiceFormDataKeys } from "@/generated/BuyInvoiceFormDataKeys";
import { buyInvoiceRelatedAttributes } from "@/formTypes/BuyInvoiceDataType";
import { getStoreId, getTerminalId } from "./Auth";

export const generateCurrencyOptions = (): Record<string, string> => {
    return Object.entries(PAYMENT_METHODS).reduce((a, [k, v]) => {
        return {
            ...a,
            [k]: v.label
        }
    }, {})
}

export const calculateGain = (formulaExpresion: string, cost: string | number): number | string => {
    let formula = formulaExpresion.replace("X", "*");
    formula = formula.replace("costo", `${cost}`);
    let subtotal = evaluate(formula);
    const numberSubtotal = Number(subtotal);
    if (isNaN(numberSubtotal)) {
        return 0
    }
    return numberSubtotal.toFixed(2)
}

export const calculateWithTaxes = (price: string | number, percentaje: string): number | string => {
    const taxtPercentaje = TAXES_PERCENTAGES_VALUES[percentaje] || 0
    let formula = `price * 1.${taxtPercentaje}`;
    formula = formula.replace("price", `${price}`);
    let subtotal = evaluate(formula);
    const numberSubtotal = Number(subtotal);
    if (isNaN(numberSubtotal)) {
        return 0
    }
    return numberSubtotal.toFixed(2)
}

export const calculateProductPrice = (product: ProductDataType, priceData?: number | PriceDataType, calculateTaxes: boolean = false): number | string => {
    const priceCode = Number(priceData);
    if (!isNaN(priceCode)) {
        const price = Number(product[`precio${priceCode}` as keyof ProductDataType])
        if (isNaN(price)) {
            return 0
        }
        return !calculateTaxes ? price.toFixed(2) : calculateWithTaxes(price.toFixed(2), `${product.porcentaje_impuesto}`)
    } else {
        if (product.costo && (priceData as PriceDataType).formula) {
            const calculatedPrice = calculateGain(`${(priceData as PriceDataType).formula}`, product.costo)
            return !calculateTaxes ? calculatedPrice : calculateWithTaxes(calculatedPrice, `${product.porcentaje_impuesto}`)
        }
    }
    return 0
}

export const formatMoneyAmount = (amount?: number): string => {
    if (!amount || isNaN(amount)) {
        return "0"
    } else {
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount);
    }
}

export const generateTemporaryProductCode = (
    productList: EDocumentProductDataType[] | undefined,
    productToAdd: EDocumentProductDataType
): string | undefined => {
    if (!productList || !productToAdd?.product?.temporal || !productToAdd.product.category?.codigo) {
        return undefined;
    }

    const categoryCode = productToAdd.product.category.codigo;
    const prefix = `${categoryCode}-A`;

    // Buscar todos los productos que ya tengan un código con ese prefijo
    const existingCodes = productList
        .map(p => p?.codigo)
        .filter((codigo): codigo is string => typeof codigo === 'string' && codigo.startsWith(prefix));

    // Extraer los números después del prefijo y encontrar el mayor
    const maxIndex = existingCodes.reduce((max, code) => {
        const match = code.match(new RegExp(`^${categoryCode}-A(\\d{3})$`));
        const number = match ? parseInt(match[1], 10) : 0;
        return number > max ? number : max;
    }, 0);

    const nextSequence = (maxIndex + 1).toString().padStart(3, '0');
    return `${prefix}${nextSequence}`;
}

export const generatePaymentWayOptions = (): Record<string, string> => {
    return Object.entries(PAYMENT_METHODS).reduce((a, [k, v]) => {
        return {
            ...a,
            [k]: v.label
        }
    }, {})
}

// export const getFechaDeEmisionRFC3339 = <T>(
//     data: T,
//     createdDateFieldName: keyof T
// ): string => {

//     let datetime = momentTz.tz("America/Costa_Rica");
//     if (data[createdDateFieldName]) {
//         datetime = momentTz.tz(`${data[createdDateFieldName]}`, "YYYY-MM-DD HH:mm:ss", "America/Costa_Rica");
//     }

//     return datetime.format(); // RFC3339
// };

export const getFechaDeEmisionRFC3339 = <T>(
    data: T,
    createdDateFieldName: keyof T
): string => {
    // 1) Detecta la zona horaria del usuario
    const userTz =
        Intl.DateTimeFormat().resolvedOptions().timeZone ||
        momentTz.tz.guess() ||
        "UTC";

    // 2) Obtiene el valor crudo del campo
    const raw = data?.[createdDateFieldName] as unknown;

    let m = momentTz.tz(userTz); // default: ahora en tz del usuario

    if (raw != null) {
        if (raw instanceof Date) {
            m = momentTz(raw).tz(userTz);
        } else if (typeof raw === "number") {
            // asume timestamp (ms). Si fuese segundos, ajustar *1000.
            m = momentTz(raw).tz(userTz);
        } else if (typeof raw === "string") {
            // Si trae offset/ISO (Z o ±hh:mm), respétalo y luego conviértelo a la tz del usuario
            const looksISOWithZone = /Z$|[+-]\d{2}:?\d{2}$/.test(raw) || raw.includes("T");
            if (looksISOWithZone) {
                m = momentTz.parseZone(raw).tz(userTz);
            } else {
                // Caso legacy sin zona: "YYYY-MM-DD HH:mm:ss" -> interprétalo en la tz del usuario
                m = momentTz.tz(raw, "YYYY-MM-DD HH:mm:ss", userTz);
            }
        }
    }

    if (!m.isValid()) {
        // Fallback seguro
        m = momentTz.tz(userTz);
    }

    // 3) Devuelve RFC3339 (ISO 8601 con offset, p.ej. 2025-08-29T13:22:19-06:00)
    return m.format();
};

const readStoredReferencesAsInfoRef = (raw?: string | null): InfoRefElement[] => {
    if (!raw) return [];
    try {
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed
            .filter((item: any) => item && typeof item === "object"
                && typeof item.numero === "string"
                && typeof item.fechaEmisionUTC === "string"
                && typeof item.tipoDoc === "string"
                && typeof item.codigo === "string"
                && typeof item.razon === "string")
            .map((item: any) => ({
                numero: item.numero,
                fechaEmisionUTC: item.fechaEmisionUTC,
                tipoDoc: item.tipoDoc,
                codigo: item.codigo,
                razon: item.razon,
            }));
    } catch {
        return [];
    }
};

export type BuildReferencedDocumentInfoInput = {
    numero: string;
    fechaEmisionUTC: string;
    tipoDoc: string;
    codigoReferencia: keyof typeof REFERENCES_CODES;
    razon: string;
};

export const buildReferencedDocumentInfo = (input: BuildReferencedDocumentInfoInput): InfoRefElement => ({
    numero: input.numero,
    fechaEmisionUTC: input.fechaEmisionUTC,
    tipoDoc: input.tipoDoc,
    codigo: REFERENCES_CODES[input.codigoReferencia],
    razon: input.razon,
});

export const buildInvoiceReference = (
    invoice: Pick<InvoiceDataType, "invoice_key" | "fecha" | "einvoice_kind">,
    codigoReferencia: keyof typeof REFERENCES_CODES,
    razon: string,
): InfoRefElement =>
    buildReferencedDocumentInfo({
        numero: `${invoice.invoice_key}`,
        fechaEmisionUTC: getFechaDeEmisionRFC3339(invoice, "fecha"),
        tipoDoc: resolveReferencedDocumentType(invoice.einvoice_kind),
        codigoReferencia,
        razon,
    });

export const buildCreditnoteReference = (
    creditnote: Pick<CreditnoteDataType, "document_key" | "created" | "description">,
    codigoReferencia: keyof typeof REFERENCES_CODES,
    razon?: string,
): InfoRefElement =>
    buildReferencedDocumentInfo({
        numero: `${creditnote.document_key}`,
        fechaEmisionUTC: getFechaDeEmisionRFC3339(creditnote, "created"),
        tipoDoc: REFERENCES_INFORMATION.Creditnote,
        codigoReferencia,
        razon: razon ?? `${creditnote.description}`,
    });

export const buildDebitnoteReference = (
    debitnote: Pick<DebitnoteDataType, "document_key" | "created" | "description">,
    codigoReferencia: keyof typeof REFERENCES_CODES,
    razon?: string,
): InfoRefElement =>
    buildReferencedDocumentInfo({
        numero: `${debitnote.document_key}`,
        fechaEmisionUTC: getFechaDeEmisionRFC3339(debitnote, "created"),
        tipoDoc: REFERENCES_INFORMATION.Debitnote,
        codigoReferencia,
        razon: razon ?? `${debitnote.description}`,
    });

export const generateATVDocumentKey = <T extends { sequence?: string }>(
    data: T,
    suscription: SuscriptionDataType,
    situacionComprobante: SituacionComprobanteType,
    createdDateFieldName: keyof T
): string => {
    const codigoPais = "506"; // Costa Rica por defecto

    // Obtener fecha en formato RFC3339 y convertir a moment
    const fechaRFC3339 = getFechaDeEmisionRFC3339(data, createdDateFieldName);
    const utcDateTime = momentTz(fechaRFC3339).tz("America/Costa_Rica");

    // Formato día, mes y año (dmy)
    const fechaGeneracion = utcDateTime.format("DDMMYY");

    // Normalizar identificación
    let numIdentificationSuscripcion =
        suscription?.identificacion || "";
    numIdentificationSuscripcion = numIdentificationSuscripcion.replace(/-/g, "");
    numIdentificationSuscripcion = numIdentificationSuscripcion.padStart(12, "0");

    const codigoSeguridad = "76457699";

    const invoiceKey = `${codigoPais}${fechaGeneracion}${numIdentificationSuscripcion}${data.sequence}${situacionComprobante}${codigoSeguridad}`;

    return invoiceKey;
};


export const isValidEDocument = (documentStatusPayload: string): boolean => {
    let isValid = true;
    try {
        if (documentStatusPayload) {
            const statusData = JSON.parse(documentStatusPayload);
            if (statusData && statusData["status"] == "rechazado") {
                isValid = false;
            }
        }
    } catch (err) {
        console.error(err)
    }
    return isValid;
}

export const generateATVDocumentSequenceNumber = (sequence: DocumentSequenceDataType) => {
    return `${sequence.store}${sequence.terminal}${sequence.kind}${sequence.number}`
}

const normalizeNumericId = (value: unknown, length: number, fallback: string): string => {
    if (typeof value !== "string" && typeof value !== "number") return fallback;
    const raw = `${value}`.trim();
    if (!raw.length || raw.length > length || !/^\d+$/.test(raw)) return fallback;
    // Hacienda no admite sucursal "000" ni terminal "00000".
    if (Number(raw) < 1) return fallback;
    return raw.padStart(length, "0");
}

/**
 * Sucursal en formato Hacienda: 3 dígitos, 001-999.
 * Valor vacío o inválido devuelve DEFAULT_STORE.
 * Mantener sincronizado con amplify/functions/save-edocument/atv.ts.
 */
export const normalizeStoreId = (value?: unknown, fallback: string = DEFAULT_STORE): string => normalizeNumericId(value, 3, fallback)

/**
 * Terminal en formato Hacienda: 5 dígitos, 00001-99999.
 * Valor vacío o inválido devuelve DEFAULT_TERMINAL.
 * Mantener sincronizado con amplify/functions/save-edocument/atv.ts.
 */
export const normalizeTerminalId = (value?: unknown, fallback: string = DEFAULT_TERMINAL): string => normalizeNumericId(value, 5, fallback)

/**
 * Resuelve la secuencia ATV del tipo de documento para la sucursal y terminal del
 * usuario autenticado. `store` y `terminal` permiten forzar valores explícitos; si se
 * omiten se leen del token de Cognito (`custom:storeId` / `custom:terminalId`).
 */
export const generateATVDocumentSequence = async (documentKind: EDocumentTypeKey, store?: string, terminal?: string): Promise<DocumentSequenceDataType> => {
    // Sin sucursal/terminal asignada (o con un valor invalido) se cae siempre a los
    // valores por defecto: un consecutivo incompleto seria rechazado por Hacienda.
    let storeId = normalizeStoreId(store, DEFAULT_STORE)
    if (store === undefined) {
        try {
            storeId = normalizeStoreId(await getStoreId(), DEFAULT_STORE);
        } catch (err) {
            storeId = DEFAULT_STORE
        }
    }
    let terminalId = normalizeTerminalId(terminal, DEFAULT_TERMINAL)
    if (terminal === undefined) {
        try {
            terminalId = normalizeTerminalId(await getTerminalId(), DEFAULT_TERMINAL);
        } catch (err) {
            terminalId = DEFAULT_TERMINAL
        }
    }
    const documentKindCode = EDOCUMENT_TYPE[documentKind];
    const { items } = await DocumentSequenceApi.fetchAll({
        and: [
            { store: { eq: storeId } },
            { terminal: { eq: terminalId } },
            { kind: { eq: documentKindCode } },
        ]
    }, "", 1000)
    if (items.length) {
        const currentSequence = items[0];
        if (currentSequence.number) {
            return {
                ...currentSequence,
                number: getNextPaddedNumber(currentSequence.number, 10)
            }
        }
    }
    return {
        kind: documentKindCode,
        terminal: terminalId,
        store: storeId,
        number: getNextPaddedNumber("0", 10)
    }
}

export const getNextPaddedNumber = (input?: string, padLength?: number) => {
    let padSize = Number(padLength || 0)
    if (!padLength) {
        padSize = input ? input.length : 0;
    }
    const numericValue = Number(input || 0);
    const incremented = numericValue + 1;
    return incremented.toString().padStart(padSize, '0');
}

export const generateCustomInformationJson = (information: Record<string, string>): string => {
    return JSON.stringify(Object.entries(information).map(([title, value]) => {
        return {
            title,
            value
        }
    })
    )
}

export const generateNewEDocumentProductsData = (documentProducts: EDocumentProductDataType[], relatedAttributes: (keyof EDocumentProductDataType)[]): EDocumentProductDataType[] => {
    return documentProducts.filter(dp => dp.cant != 0).map(dp => {
        const product = omitProperties(dp, [...relatedAttributes, "id"]) as EDocumentProductDataType
        return product
    });
}

export const generateDebitNoteFromCreditnote = (creditNote: CreditnoteDataType, razon: string): { debitnote: DebitnoteDataType, products: EDocumentProductDataType[] } => {
    const products = creditNote.creditnoteProducts;

    const debitnoteProducts = generateNewEDocumentProductsData(products as EDocumentProductDataType[], [...CreditNoteProductRelatedAttributes, "id", "creditnoteId", "createdAt", "updatedAt"])
    const debitNote = omitProperties({
        ...creditNote,
        description: razon
    }, [...CreditnoteRelatedAttributes, "id", "sequence", "document_key", "numero", "need_assignment", "edocument_status_payload", "createdAt", "updatedAt"], true)

    return {
        debitnote: {
            ...debitNote,
            description: razon,
            created: getCurrentDateTime(),
        },
        products: debitnoteProducts
    }
}

export const generateCreditFromDebitnote = (debitnote: DebitnoteDataType, razon: string): { creditnote: CreditnoteDataType, products: EDocumentProductDataType[] } => {
    const products = debitnote.debitnoteProducts;

    const creditnoteProducts = generateNewEDocumentProductsData(products as EDocumentProductDataType[], [...DebitnoteProductRelatedAttributes, "id", "debitnoteId", "createdAt", "updatedAt"])
    const creditNote = omitProperties(debitnote, [...DebitnoteRelatedAttributes, "id", "sequence", "document_key", "numero", "applied", "edocument_status_payload", "createdAt", "updatedAt"], true)

    return {
        creditnote: {
            ...creditNote,
            description: razon,
            created: getCurrentDateTime(),
        },
        products: creditnoteProducts
    }
}

export const generatePriceCreditFromInvoice = (invoice: InvoiceDataType, razon: string, amount: number): { creditnote: CreditnoteDataType, products: EDocumentProductDataType[] } => {
    const products = invoice.invoiceProducts;

    const invoiceUnneedFields = getMissingAttributes(CreditnoteFormDataKeys, InvoiceFormDataKeys);
    const additionaInvoiceProps = [...invoiceUnneedFields, "id", "sequence", "numero", "edocument_status_payload", "createdAt", "updatedAt"]

    const creditnoteProducts = generateNewEDocumentProductsData(products as EDocumentProductDataType[], [...InvoiceProductRelatedAttributes, "id", "invoiceId", "createdAt", "updatedAt"])
    const generatedCreditNote = omitProperties(invoice, [...InvoiceRelatedAttributes, ...additionaInvoiceProps], true)

    const documentData: Partial<EDocumentState> = {
        documentProducts: creditnoteProducts.map(p => {
            p.selected = "true";
            p.cantFact = p.cant;
            p.priceFact = p.price;
            p.subtotalFact = p.subtotal;
            p.descuentoFact = p.descuento;
            p.impuestosFact = p.impuestos;
            p.descriptionFact = p.description;
            p.impuestos_servicioFact = p.impuestos_servicio;
            return p;
        }),
        documentNoteTotal: amount
    }

    updateCreditNoteDataState(documentData as EDocumentState)
    const documentSummary = calculateDocumentGranTotals(documentData.documentProducts as EDocumentProductDataType[])
    // Proyeccion explicita en vez de spread + lista negra: el resumen tiene campos
    // que no son columnas del modelo (impuestosExentos, otrosCargos) y cualquiera
    // nuevo romperia el insert de Amplify en silencio.
    //
    // `otros_cargos` e `impuestos_servicio` se limpian a proposito: esta nota es por
    // monto (revierte el descuento), y su `total` sale de las lineas sin cargos. Si
    // heredara los de la factura, el XML sumaria un TotalOtrosCargos que el total
    // guardado no tiene y el TotalComprobante no cuadraria.
    const creditnote = {
        ...generatedCreditNote,
        description: razon,
        created: getCurrentDateTime(),
        invoiceId: invoice.id,
        subtotal: documentSummary.subtotal,
        descuento: documentSummary.descuento,
        impuestos: documentSummary.impuestos,
        total: documentSummary.total,
        otros_cargos: serializeStoredOtherCharges([]),
        impuestos_servicio: 0,
    }

    const formatedProducts = (documentData.documentProducts || []).map(p =>
        omitProperties(p, ["selected", "cantFact", "descuentoFact", "descriptionFact", "priceFact", "subtotalFact", "impuestosFact", "impuestos_servicioFact"])
    )

    return {
        creditnote,
        products: formatedProducts
    }
}

export const generateCreditFromBuyInvoice = (buyInvoice: BuyInvoiceDataType, razon: string): { creditnote: CreditnoteDataType, products: EDocumentProductDataType[] } => {
    const products = buyInvoice.buyInvoiceProducts;

    const invoiceUnneedFields = getMissingAttributes(CreditnoteFormDataKeys, BuyInvoiceFormDataKeys);
    const additionaInvoiceProps = [...invoiceUnneedFields, "id", "sequence", "numero", "edocument_status_payload", "createdAt", "updatedAt"]

    const creditnoteProducts = generateNewEDocumentProductsData(products as EDocumentProductDataType[], [...BuyInvoiceProductRelatedAttributes, "id", "buyInvoiceId", "createdAt", "updatedAt"])
    const creditNote = omitProperties(buyInvoice, [...buyInvoiceRelatedAttributes, ...additionaInvoiceProps], true)

    return {
        creditnote: {
            ...creditNote,
            description: razon,
            created: getCurrentDateTime(),
            buyInvoiceId: buyInvoice.id
        },
        products: creditnoteProducts
    }
}

/**
 * Nota de credito total (anulacion). Hereda `otros_cargos` de la factura via
 * `omitProperties`, y tiene que hacerlo: si la factura cobro un impuesto de servicio o
 * un timbre, la nota que la revierte debe devolver tambien ese monto, o el
 * TotalComprobante de la nota no cuadraria con el de la factura anulada.
 */
export const generateCreditFromInvoice = (invoice: InvoiceDataType, razon: string): { creditnote: CreditnoteDataType, products: EDocumentProductDataType[] } => {
    const products = invoice.invoiceProducts;

    const invoiceUnneedFields = getMissingAttributes(CreditnoteFormDataKeys, InvoiceFormDataKeys);
    const additionaInvoiceProps = [...invoiceUnneedFields, "id", "sequence", "numero", "edocument_status_payload", "createdAt", "updatedAt"]

    const creditnoteProducts = generateNewEDocumentProductsData(products as EDocumentProductDataType[], [...InvoiceProductRelatedAttributes, "id", "invoiceId", "createdAt", "updatedAt"])
    const creditNote = omitProperties(invoice, [...InvoiceRelatedAttributes, ...additionaInvoiceProps], true)

    return {
        creditnote: {
            ...creditNote,
            description: razon,
            created: getCurrentDateTime(),
            invoiceId: invoice.id
        },
        products: creditnoteProducts
    }
}

export const sendEInvoiceData = async (easyposEInvoice: EasyposEInvoice, invoiceId: string, references?: InfoRefElement[]): Promise<ATVResponseBody> => {
    const invoiceData = await InvoiceApi.get(invoiceId, {
        selectionSet: [
            ...InvoiceFormDataKeys.filter(k => !InvoiceRelatedAttributes.includes(k)),
            "currency.*",
            "client.*",
            "invoiceProducts.*",
            "invoicePayments.*",
            "invoiceProducts.product.*",
            "saleman.*",
        ]
    })
    const referencesToSend = references && references.length > 0
        ? references
        : readStoredReferencesAsInfoRef(invoiceData.info_references);
    return await easyposEInvoice.submitEInvoice(invoiceData, referencesToSend);
}

export const sendEBuyInvoiceData = async (easyposEInvoice: EasyposEInvoice, invoiceId: string, references: InfoRefElement[] = []): Promise<ATVResponseBody> => {
    const invoiceData = await BuyInvoiceApi.get(invoiceId, {
        selectionSet: [
            ...BuyInvoiceFormDataKeys.filter(k => !buyInvoiceRelatedAttributes.includes(k)),
            "currency.*",
            "buyInvoiceProducts.*",
        ]
    })

    const storedRefs = readStoredReferencesAsInfoRef(invoiceData.info_references);
    if (storedRefs.length > 0) {
        references.push(...storedRefs);
    }

    const hasReferenceForProvider = references.some(
        (r) => r.tipoDoc === (invoiceData.ref_doctype || "") && r.numero === (invoiceData.ref_numero || "")
    );
    if (!hasReferenceForProvider) {
        const documentRef: InfoRefElement = {
            codigo: invoiceData.ref_codigo || "",
            fechaEmisionUTC: getFechaDeEmisionRFC3339(invoiceData, "ref_date"),
            numero: invoiceData.ref_numero || "",
            razon: invoiceData.ref_razon || "",
            tipoDoc: invoiceData.ref_doctype || ""
        }
        references.push(documentRef)
    }

    const provider = await ProviderApi.get(`${invoiceData.providerId}`)
    const { items: payments } = await BuyInvoicePaymentApi.fetchAllByBuyInvoice(invoiceId)
    return await easyposEInvoice.submitEBuyInvoice({
        ...invoiceData,
        provider,
        buyInvoicePayments: payments
    }, references);
}

/**
 * Carga la nota y la factura que referencia con las relaciones que necesita el payload del
 * ATV. Compartido por el envio (`sendECreditnoteData`) y por el armado sin enviar
 * (`buildECreditnoteRequest`), para que ambos usen exactamente los mismos selection sets.
 */
const loadCreditnoteSubmissionContext = async (noteId: string, references?: InfoRefElement[], expectedLineCount?: number) => {
    const readCreditnote = () => CreditnoteApi.get(noteId, {
        selectionSet: [
            ...CreditnoteFormDataKeys.filter(k => !CreditnoteRelatedAttributes.includes(k)),
            "currency.*",
            "client.*",
            "creditnoteProducts.*",
            "creditnoteProducts.product.*",
        ]
    })
    // Al armar el payload de una nota recien creada las lineas se leen por GSI, que es
    // eventualmente consistente: sin esperarlas el comprobante saldria incompleto.
    const creditnoteData = expectedLineCount
        ? await fetchDocumentWithCompleteLines(
            readCreditnote,
            (note) => note.creditnoteProducts?.length || 0,
            expectedLineCount,
        )
        : await readCreditnote();
    const invoiceData = await InvoiceApi.get(`${creditnoteData.invoiceId}`, {
        selectionSet: [
            ...InvoiceFormDataKeys.filter(k => !InvoiceRelatedAttributes.includes(k)),
            "currency.*",
            "client.*",
            "invoiceProducts.*",
            "invoicePayments.*",
            "invoiceProducts.product.*",
            "saleman.*",
        ]
    })
    const referencesToSend = references && references.length > 0
        ? references
        : readStoredReferencesAsInfoRef(creditnoteData.info_references);
    return { creditnoteData, invoiceData, referencesToSend };
}

/**
 * Payload del ATV de una nota de credito ligada a una factura, sin enviarlo. Lo usa el
 * descuento por nota de credito: la nota se guarda con este JSON en `pending_submission` y
 * la lambda submit-pending-enote lo envia cuando Hacienda ya registro la factura.
 */
export const buildECreditnoteRequest = async (easyposEInvoice: EasyposEInvoice, noteId: string, references?: InfoRefElement[], expectedLineCount?: number): Promise<ApiENoteRequest> => {
    const { creditnoteData, invoiceData, referencesToSend } = await loadCreditnoteSubmissionContext(noteId, references, expectedLineCount);
    return easyposEInvoice.buildCreditnoteRequest(invoiceData, creditnoteData, referencesToSend);
}

export const sendECreditnoteData = async (easyposEInvoice: EasyposEInvoice, noteId: string, references?: InfoRefElement[], sourceDocument: "Invoice" | "BuyInvoice" = "Invoice"): Promise<ATVResponseBody> => {
    if (sourceDocument == "Invoice") {
        const { creditnoteData, invoiceData, referencesToSend } = await loadCreditnoteSubmissionContext(noteId, references);
        return await easyposEInvoice.submitCreditnote(invoiceData, creditnoteData, referencesToSend);
    } else {
        const creditnoteData = await CreditnoteApi.get(noteId, {
            selectionSet: [
                ...CreditnoteFormDataKeys.filter(k => !CreditnoteRelatedAttributes.includes(k)),
                "currency.*",
                "client.*",
                "creditnoteProducts.*",
                "creditnoteProducts.product.*",
            ]
        })
        const referencesToSend = references && references.length > 0
            ? references
            : readStoredReferencesAsInfoRef(creditnoteData.info_references);
        const buyInvoiceData = await BuyInvoiceApi.get(`${creditnoteData.buyInvoiceId}`, {
            selectionSet: [
                ...BuyInvoiceFormDataKeys.filter(k => !buyInvoiceRelatedAttributes.includes(k)),
                "currency.*",
                "buyInvoiceProducts.*",
            ]
        })
        const provider = await ProviderApi.get(`${buyInvoiceData.providerId}`);
        const { items: buyInvoicePayments } = await BuyInvoicePaymentApi.fetchAllByBuyInvoice(`${buyInvoiceData.id}`);
        const buyInvoice: BuyInvoiceDataType = {
            ...buyInvoiceData,
            provider: provider,
            buyInvoicePayments: buyInvoicePayments
        }
        return await easyposEInvoice.submitBuyInvoiceCreditnote(buyInvoice, creditnoteData, referencesToSend);
    }
}

export const sendEDebitnoteData = async (easyposEInvoice: EasyposEInvoice, noteId: string, references?: InfoRefElement[]): Promise<ATVResponseBody> => {
    const debitnote = await DebitnoteApi.get(noteId, {
        selectionSet: [
            ...DebitnoteFormDataKeys.filter(k => !DebitnoteRelatedAttributes.includes(k)),
            "currency.*",
            "client.*",
            "debitnoteProducts.*",
            "debitnoteProducts.product.*",
        ]
    })
    const invoiceData = await InvoiceApi.get(`${debitnote.invoiceId}`, {
        selectionSet: [
            ...InvoiceFormDataKeys.filter(k => !InvoiceRelatedAttributes.includes(k)),
            "currency.*",
            "client.*",
            "invoiceProducts.*",
            "invoicePayments.*",
            "invoiceProducts.product.*",
            "saleman.*",
        ]
    })

    const referencesToSend = references && references.length > 0
        ? references
        : readStoredReferencesAsInfoRef(debitnote.info_references);
    return await easyposEInvoice.submitDebitnote(invoiceData, debitnote, referencesToSend);
}

/**
 * Orden canonico de las lineas de un documento: el `orderIndex` que el slice mantiene al capturar.
 * Lo usan por igual las vistas de impresion y el payload del ATV (`generateDocumentProducs`), para
 * que el `NumeroLinea` del XML coincida con el orden que ve el usuario en la factura.
 *
 * El sort es **estable a proposito**: no se le agrega un desempate por `id`. Los documentos emitidos
 * antes de que existiera `orderIndex` tienen todas las lineas en `null` y caen al orden en que los
 * devuelve DynamoDB; ese es el orden con el que se les genero el XML, y un desempate lo cambiaria
 * solo en la reimpresion.
 */
export const sortProductsByOrder = (products?: EDocumentProductDataType[]): EDocumentProductDataType[] => {
    if (products) {
        const newProducts = [...products]
        newProducts.sort((a: EDocumentProductDataType, b: EDocumentProductDataType) => Number(a.orderIndex || 0) - Number(b.orderIndex || 0));
        return newProducts
    }
    return []
}

type EdocumentSequencesReturn = {
    sequence: DocumentSequenceDataType,
    sequenceNumber: string,
    documentKey: string,
    generatedNumber: DocumentAutoGeneratedNumberDataType
}

export const generateEdocumentSequences = async <T extends { sequence?: string }>(
    data: T,
    modelName: keyof ApiSchema | "ExportInvoice",
    createdDateFieldName: keyof T,
    suscriptionData: SuscriptionDataType,
    overrideSequenceModel?: EDocumentTypeKey
): Promise<EdocumentSequencesReturn> => {

    let sequence: DocumentSequenceDataType = {}
    let sequenceNumber = "";
    let documentKey = "";

    if (suscriptionData?.einvoice_status) {
        const sequenceModel = overrideSequenceModel || modelName;
        sequence = await generateATVDocumentSequence(`${sequenceModel}` as EDocumentTypeKey)
        sequenceNumber = generateATVDocumentSequenceNumber(sequence);
        documentKey = generateATVDocumentKey({
            ...data,
            sequence: sequenceNumber
        }, suscriptionData, SITUACION_COMPROBANTE_NORMAL, createdDateFieldName)
    }

    let generatedNumber = await generateNextNumberByModel(modelName);
    if (!generatedNumber?.number) {
        throw Error(`No fue posible generar el identificador del ${modelName}`)
    }

    return {
        sequence,
        sequenceNumber,
        documentKey,
        generatedNumber
    }
}

export type XMLDocumentData = {
    fecha: string,
    total: number,
    identificacion: string,
    tipo: string,
    pagada: boolean,
    numero: string,
    sequence: string,
    document_key: string,
    impuestos: number,
    subtotal: number,
    descuento: number,
    exchange_rate: number,
    providerId?: string | null,
    currencyId: string,
    Products: XMLDocumentProductData[]
}

type XMLDocumentProductData = {
    cant: number,
    codigo: string,
    costo: number,
    descripcion: string,
    originalCosto: number
}

const mapXmlProductToDocumentProductData = (product: any, exchangeRate: number) => {
    const productCod = product?.CodigoComercial?.Codigo || product?.Codigo?.Codigo || product?.Codigo || "N/A"

    let precioFinal = Number(product?.PrecioUnitario || 0)
    if (exchangeRate != 1) {
        precioFinal = Number(product?.PrecioUnitario || 0) * exchangeRate
    }

    const productLine: XMLDocumentProductData = {
        codigo: productCod,
        descripcion: product?.Detalle || "N/A",
        cant: roundCant(Number(product?.Cantidad || "0")),
        originalCosto: precioFinal,
        costo: precioFinal
    }
    return productLine
}

const loadXmlProducts = async (fileData: any, exchangeRate: number): Promise<XMLDocumentProductData[]> => {
    const lineasDetalle = findPropertyRecursive(fileData, "LineaDetalle")
    if (lineasDetalle instanceof Array) {
        return lineasDetalle.map(ld => {
            return mapXmlProductToDocumentProductData(ld, exchangeRate)
        })

    }
    if (lineasDetalle instanceof Object) {
        const productLine = mapXmlProductToDocumentProductData(lineasDetalle, exchangeRate)
        return [productLine]
    }
    return []
}

export type InformacionReferencia = {
    TipoDocIR: string,
    Numero: string,
    FechaEmisionIR: string,
    Codigo: string,
    Razon: string,
}

export type LoadXmlDocumentReponse = {
    providerInvoiceData: Partial<ProviderInvoiceDataType>,
    messageData: ProviderInvoiceMessageFormData,
    documentSummary: XmlSummaryData,
    documentData: XMLDocumentData,
}

export type XmlSummaryData = {
    moneda: string
    fechaEmision: string,
    emisor: any,
    receptor: any,
    providerName: string,
    totalImpuesto: string,
    documentKey: string,
    documentType: ValidateableEdocument,
    totalComprobante: string,
    totalComprobanteAmount: string,
    tipo_identificacion: string,
    identificacion: string,
}

export const ValidateableEdocumentKinds = ["NotaCreditoElectronica", "FacturaElectronica", "NotaDebitoElectronica"];
export type ValidateableEdocument = "NotaCreditoElectronica" | "FacturaElectronica" | "NotaDebitoElectronica"

export const loadXmlDocument = async (fileData: any, documentType?: ValidateableEdocument[]): Promise<Partial<LoadXmlDocumentReponse>> => {
    if (documentType && !Object.keys(fileData).some(f => documentType.includes(f as ValidateableEdocument))) {
        throw new Error(`El documento a validar no es una ${documentType.join(" o ")}`)
    }

    const fechaEmision = moment(findPropertyRecursive(fileData, "FechaEmision"));
    const emisor = findPropertyRecursive(fileData, "Emisor");
    const providerIdentification = `${(emisor?.Identificacion?.Numero || "")}`;
    const condicionVenta = findPropertyRecursive(fileData, "CondicionVenta")
    const resumenFactura = findPropertyRecursive(fileData, "ResumenFactura")
    const tipoDeCambio = Number(toNumberFormat(resumenFactura?.CodigoTipoMoneda?.TipoCambio || 1));

    let provider: ProviderDataType = {}
    try {
        provider = await ProviderApi.getProviderByIdentificacion(`${providerIdentification}`);
    } catch (err) { }
    const currencyCode = resumenFactura?.CodigoTipoMoneda?.CodigoMoneda || "CRC"
    const currencyCodeId = CURRENCIES_CODE_IDS[currencyCode];
    let currentData = await CurrencyApi.find({ currency_id: { eq: currencyCodeId } });
    if (!currentData.id) {
        currentData = await CurrencyApi.getDefaultCurrency()
        showAlert("warning", `La moneda de esta factura (${currencyCode}) no esta registrada en el sistema, se asigno la moneda por defecto del sistema`)
    }

    const products = await loadXmlProducts(fileData, tipoDeCambio)

    let defaultData: XMLDocumentData = {
        fecha: fechaEmision.format("YYYY-MM-DD"),
        total: Number(toNumberFormat(resumenFactura?.TotalComprobante)),
        identificacion: providerIdentification,
        tipo: `${condicionVenta}`.padStart(2, "0") == "02" ? "02" : "01",
        pagada: condicionVenta === '01', //"Contado",
        numero: findPropertyRecursive(fileData, "NumeroConsecutivo"),
        sequence: findPropertyRecursive(fileData, "NumeroConsecutivo"),
        document_key: findPropertyRecursive(fileData, "Clave"),
        impuestos: Number(toNumberFormat(resumenFactura?.TotalImpuesto)),
        subtotal: Number(toNumberFormat(resumenFactura?.TotalVenta)),
        descuento: Number(toNumberFormat(resumenFactura?.TotalDescuentos || 0)),
        exchange_rate: Number(toNumberFormat(resumenFactura?.CodigoTipoMoneda?.TipoCambio || 1)),
        currencyId: `${currentData.id}`,
        providerId: provider?.id || null,
        Products: products
    }

    const xmldDocument = omitProperties(defaultData, [], true)
    const imporData = await generateImportedEdocumentData(xmldDocument, fileData)
    return {
        ...imporData,
        documentData: xmldDocument
    }

}

const generateImportedEdocumentData = async (documentData: XMLDocumentData, fileData: any): Promise<Partial<LoadXmlDocumentReponse>> => {
    const defaultCategoria = await ProviderInvoiceCategoryApi.find({ is_default: { eq: true } });
    const defaultExpenseCategory = await ExpenseCategoryApi.find({ is_default: { eq: true } });
    const providerInvoiceData: Partial<ProviderInvoiceDataType> = omitProperties(documentData, ["Products", "identificacion"])

    const documentTypes: string[] = Object.keys(fileData).filter(k => ValidateableEdocumentKinds.includes(k));

    const moneda = findPropertyRecursive(fileData, "CodigoMoneda") || "CRC";
    const totalImpuesto = findPropertyRecursive(fileData, "TotalImpuesto") || "0";
    const totalComprobante = findPropertyRecursive(fileData, "TotalComprobante") || "0";
    const clave = findPropertyRecursive(fileData, "Clave");
    const fechaEmision = findPropertyRecursive(fileData, "FechaEmision");
    const codigoActividadReceptor = findPropertyRecursive(fileData, "CodigoActividadReceptor");
    const emisor = findPropertyRecursive(fileData, "Emisor")
    const receptor = findPropertyRecursive(fileData, "Receptor")
    const documentSummary: XmlSummaryData = {
        moneda,
        fechaEmision,
        emisor,
        receptor,
        providerName: emisor?.Nombre || "N/A",
        totalImpuesto: `${moneda} ${totalImpuesto}`,
        documentKey: `${clave}`,
        totalComprobante: `${moneda} ${totalComprobante}`,
        totalComprobanteAmount: `${totalComprobante}`,
        tipo_identificacion: `${emisor.Identificacion.Tipo}`,
        identificacion: `${emisor.Identificacion.Numero}`,
        documentType: `${(documentTypes[0] || "")}` as ValidateableEdocument
    }

    const messageData: ProviderInvoiceMessageFormData = {
        condicionImpuesto: TAXES_CONDITIONS_GENERAL_CREDITO_IVA_CODE,
        providerId: documentData?.providerId || "",
        impuestoAcreditar: totalImpuesto || 0,
        gastoAplicable: "0",
        numero: providerInvoiceData.sequence,
        providerInvoiceCategoryId: defaultCategoria?.id || "",
        expenseCategoryId: defaultExpenseCategory?.id || "",
        tipo: Number(providerInvoiceData?.tipo || 1),
        mensaje: ATV_ACCEPTED_RESPONSE_CODE,
        activitycode: codigoActividadReceptor
    }

    return {
        messageData,
        documentSummary,
        providerInvoiceData
    }

}

export const resendAtvDocument = async (requestData: OpenSendAtvDocumentPubSubPayload): Promise<Response> => {
    let parsedRequest = requestData

    if (parsedRequest.emailBody) {
        parsedRequest.emailBody = safeEncodeBase64(requestData.emailBody)
    }
    const pdfResponse = await fetch("https://5wpoygsnfk.execute-api.us-west-2.amazonaws.com/prod/sendAtvDocument", {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "x-api-key": "MaeJHcOG1q2ia0P5BRPxb8xcGNrV6cST9OaDNRDg"
        },
        body: JSON.stringify(requestData)
    });
    return pdfResponse
}
